import { Check, Plus, Search } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal, flushSync } from "react-dom";
import { useContacts, useContactTypeahead } from "../../hooks/useContacts.tsx";
import { cn, isValidEmail } from "../../lib/utils.ts";
import type { Contact, Handle } from "../../types/contact.ts";
import { AvatarWithFallback } from "./avatar.tsx";

/**
 * Mirror the server's prefix typeahead match (simple lexer: every query term
 * must prefix some token of the contact name) so stale placeholder results that
 * no longer match the current input can be pruned client-side while the fresh
 * results load. Splitting on non-alphanumerics tracks the lexer's tokenization
 * (hyphens, "@", etc.); AND-ing the terms mirrors preparePrefixTsQuery.
 */
const matchesPrefixQuery = (contact: Contact, query: string): boolean => {
  const terms = query
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  if (terms.length === 0) return true;
  const tokens = (contact.name ?? "")
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  return terms.every((term) => tokens.some((token) => token.startsWith(term)));
};

export interface ContactDropdownPosition {
  top: number;
  left: number;
  width?: number;
  // Optional: if provided, positions the bottom of dropdown at this Y coordinate
  anchorBottom?: number;
}

export interface ContactAutocompleteDropdownRef {
  handleArrowUp: () => void;
  handleArrowDown: () => void;
  handleEnter: () => boolean; // Returns true if an item was selected
}

interface ContactAutocompleteDropdownBaseProps {
  query: string;
  onSelect: (contact: Contact) => void;
  onSelectEmail?: (email: string) => void;
  className?: string;
  filterHandleTypes?: Array<"emailAddress" | "phoneNumber" | "address">;
  /** When provided, show check/highlight on selected rows (multi-select mode). */
  selectedContactIds?: Set<string>;
  /** Show all contacts when query is empty (default: false — hides dropdown). */
  showAllWhenEmpty?: boolean;
  /** When provided (and query is empty), show a "Recent" section followed by "All". */
  recentContactIds?: string[];
  /** Extra contacts to merge into the list (e.g. individually-fetched recent contacts not in the first page). */
  extraContacts?: Contact[];
  /** When provided, renders a row at index 0 for free-text input.
   *  - "selected" variant (default): checkmark + search icon, shown in "Selected" section header (navigate-back).
   *  - "use" variant: plus icon, shown without section header (typing mode). */
  selectedFreeText?: {
    label: string;
    onSelect: () => void;
    variant?: "selected" | "use";
  };
  /** Called when a specific handle is selected. Each contact is expanded into one row per matching handle. */
  onSelectHandle?: (contact: Contact, handle: Handle) => void;
  /** Called when the current query's fresh server results leave nothing to select. */
  onNoMatches?: () => void;
}

/** Portal mode — renders at a specific position on the page. */
type PortalProps = ContactAutocompleteDropdownBaseProps & {
  inline?: false;
  position: ContactDropdownPosition;
};

/** Inline mode — no portal, parent controls scrolling. */
type InlineProps = ContactAutocompleteDropdownBaseProps & {
  inline: true;
  position?: never;
};

type ContactAutocompleteDropdownProps = PortalProps | InlineProps;

// ---------------------------------------------------------------------------
// Flat items array — single source of truth for list structure
// ---------------------------------------------------------------------------

type ListItem =
  | { type: "header"; label: string }
  | {
      type: "freeText";
      label: string;
      onSelect: () => void;
      variant: "use" | "selected";
    }
  | { type: "contact"; contact: Contact; isSelected: boolean }
  | { type: "handle"; contact: Contact; handle: Handle; isSelected: boolean }
  | { type: "email"; query: string }
  | { type: "sentinel" };

// ---------------------------------------------------------------------------
// EmailOption sub-component
// ---------------------------------------------------------------------------

interface EmailOptionProps {
  query: string;
  isSelected: boolean;
  onSelect: (email: string) => void;
  /** Show a + button (multi-select mode) instead of row-click only. */
  multiSelect?: boolean;
}

const EmailOption = ({ query, isSelected, onSelect, multiSelect }: EmailOptionProps) => {
  const isEmailValid = isValidEmail(query);

  const baseStyles = "flex flex-row items-center gap-2 px-3 py-2 border-t border-border";
  const validStyles = isSelected ? "bg-secondary" : "";
  const invalidStyles = "text-muted-foreground cursor-not-allowed opacity-60";

  return (
    <div
      className={cn(
        baseStyles,
        isEmailValid ? cn("text-foreground cursor-pointer", validStyles) : invalidStyles,
      )}
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect(query);
      }}
    >
      <AvatarWithFallback
        size="sm"
        person={{
          name: query,
          picture: undefined,
          value: query,
        }}
      />
      <div className="flex flex-col min-w-0 flex-1">
        <div className="text-sm font-medium truncate">
          {isEmailValid ? query : "Enter a valid email address"}
        </div>
        <div className="text-xs text-muted-foreground truncate">Add as new recipient</div>
      </div>
      {multiSelect && isEmailValid && (
        <button
          type="button"
          className="shrink-0 p-1 rounded-full hover:bg-secondary text-green-600 transition-colors"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelect(query);
          }}
          aria-label={`Add ${query}`}
        >
          <Plus className="size-4" />
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Dropdown component that displays filtered contacts based on a query string.
 * Uses a portal to render at a specific position on the page.
 * Supports keyboard navigation with arrow keys and enter.
 */
export const ContactAutocompleteDropdown = forwardRef<
  ContactAutocompleteDropdownRef,
  ContactAutocompleteDropdownProps
>(
  (
    {
      query,
      position,
      onSelect,
      onSelectEmail,
      className,
      filterHandleTypes = ["emailAddress"],
      inline,
      selectedContactIds,
      showAllWhenEmpty,
      recentContactIds,
      extraContacts,
      selectedFreeText,
      onSelectHandle,
      onNoMatches,
    },
    ref,
  ) => {
    const {
      contacts: localContacts,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
    } = useContacts();
    const {
      contacts: searchResults,
      isStale,
      isLoading: isSearching,
      error: searchError,
    } = useContactTypeahead(query);
    const hasQuery = query.trim().length > 0;
    const dropdownRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    // When true, suppress onMouseEnter index updates. Set by keyboard
    // handlers, cleared on actual mouse movement. Prevents DOM shifts
    // (from async data loading or scrollToItem) from triggering false
    // mouseenter events that hijack the keyboard-driven highlight.
    const usingKeyboardRef = useRef(false);
    const pos = inline ? { top: 0, left: 0 } : position;
    const [adjustedTop, setAdjustedTop] = useState(pos.top);
    const [rawSelectedIndex, setRawSelectedIndex] = useState(0);

    // For an active query, show server (prefix typeahead) results only — never the
    // local list. keepPreviousData (see contactQueries.typeahead) holds the prior
    // results in place while the next query loads, so the dropdown narrows smoothly
    // instead of flashing the broader, substring-matched, un-person-filtered local
    // list on the first (uncached) keystroke. Local contacts are used only for the
    // empty-query browse (Recent/All display).
    //
    // While those held-over results are stale, prune the ones that no longer match
    // the current input so obvious mismatches drop immediately instead of lingering
    // until the server responds. Fresh server results are authoritative, so they're
    // never re-filtered.
    const liveResults = useMemo(
      () =>
        isStale && hasQuery
          ? searchResults.filter((c) => matchesPrefixQuery(c, query))
          : searchResults,
      [isStale, hasQuery, searchResults, query],
    );
    const rawBaseContacts = hasQuery ? liveResults : localContacts;

    // Merge extra contacts (e.g. individually-fetched recent contacts beyond the first page).
    // Only merge when showing all contacts (empty query) — during search, the server
    // already returns matching results and unfiltered extras would pollute the list.
    const baseContacts = useMemo(() => {
      if (hasQuery || !extraContacts?.length) return rawBaseContacts;
      const ids = new Set(rawBaseContacts.map((c) => c.id));
      const extra = extraContacts.filter((c) => !ids.has(c.id));
      if (extra.length === 0) return rawBaseContacts;
      return [...rawBaseContacts, ...extra];
    }, [rawBaseContacts, extraContacts, hasQuery]);

    // Filter contacts by handle type
    const filteredByTypeContacts = useMemo(() => {
      return baseContacts.filter((c) => c.handles?.some((h) => filterHandleTypes.includes(h.type)));
    }, [baseContacts, filterHandleTypes]);

    // Filter suggestions based on query
    const filteredSuggestions = useMemo(() => {
      if (filteredByTypeContacts.length === 0) return [];
      if (!query && showAllWhenEmpty) return filteredByTypeContacts;
      if (!query) return [];

      // Active query: results come from the server, already filtered by query (the
      // handle-type filter is applied above). No client-side substring fallback —
      // its looser matching over the un-person-filtered local list is what made the
      // dropdown overshoot and then collapse on the first keystroke.
      return filteredByTypeContacts;
    }, [query, filteredByTypeContacts, showAllWhenEmpty]);

    // Check if email option should be shown
    const showEmailOption = useMemo(() => {
      if (!onSelectEmail || !filterHandleTypes.includes("emailAddress")) {
        return false;
      }
      if (!query?.trim() || !isValidEmail(query.trim())) return false;
      // Hide if there's already an exact match for this email
      const emailLower = query.trim().toLowerCase();
      return !filteredByTypeContacts.some((c) =>
        c.handles?.some((h) => h.type === "emailAddress" && h.value.toLowerCase() === emailLower),
      );
    }, [onSelectEmail, filterHandleTypes, query, filteredByTypeContacts]);

    // Show Recent/All sections when empty query and recentContactIds provided
    const showSections = Boolean(recentContactIds?.length && !query && showAllWhenEmpty);

    // --- Build the flat items array ---
    const items: ListItem[] = useMemo(() => {
      const result: ListItem[] = [];

      // Push one row per matching handle for each contact
      const pushContact = (c: Contact, isSelected: boolean) => {
        const handles = c.handles?.filter((h) => filterHandleTypes.includes(h.type)) ?? [];
        for (const handle of handles) {
          result.push({ type: "handle", contact: c, handle, isSelected });
        }
      };

      // Partition contacts into selected/unselected
      const selected =
        selectedContactIds && selectedContactIds.size > 0
          ? filteredSuggestions.filter((c) => selectedContactIds.has(c.id))
          : [];
      const unselected =
        selectedContactIds && selectedContactIds.size > 0
          ? filteredSuggestions.filter((c) => !selectedContactIds.has(c.id))
          : filteredSuggestions;

      // Email option at the top when the query is a valid email not in results
      if (showEmailOption) {
        result.push({ type: "email", query });
      }

      // Free-text "use" variant: plus icon row at the top (no section header)
      if (selectedFreeText?.variant === "use") {
        result.push({
          type: "freeText",
          label: selectedFreeText.label,
          onSelect: selectedFreeText.onSelect,
          variant: "use",
        });
      }

      // "Selected" section: free-text "selected" variant + selected contacts
      const hasSelectedSection =
        selectedFreeText?.variant !== "use" && (selectedFreeText || selected.length > 0);
      if (hasSelectedSection) {
        result.push({ type: "header", label: "Selected" });
        if (selectedFreeText) {
          result.push({
            type: "freeText",
            label: selectedFreeText.label,
            onSelect: selectedFreeText.onSelect,
            variant: selectedFreeText.variant ?? "selected",
          });
        }
        for (const c of selected) {
          pushContact(c, true);
        }
      }

      // Recent/All sections or flat unselected list
      if (showSections && recentContactIds?.length) {
        const recent: Contact[] = [];
        for (const id of recentContactIds) {
          const contact = unselected.find((c) => c.id === id);
          if (contact) recent.push(contact);
        }
        const all = [...unselected].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
        if (recent.length > 0) {
          result.push({ type: "header", label: "Recent" });
          for (const c of recent) {
            pushContact(c, false);
          }
        }
        if (all.length > 0) {
          result.push({ type: "header", label: "All" });
          for (const c of all) {
            pushContact(c, false);
          }
        }
      } else if (unselected.length > 0) {
        for (const c of unselected) {
          pushContact(c, false);
        }
      }

      // Infinite scroll sentinel
      if (!hasQuery && hasNextPage) {
        result.push({ type: "sentinel" });
      }

      return result;
    }, [
      selectedFreeText,
      selectedContactIds,
      filteredSuggestions,
      showSections,
      recentContactIds,
      showEmailOption,
      query,
      filterHandleTypes,
      hasQuery,
      hasNextPage,
    ]);

    // Selectable items = indices of non-header, non-sentinel items
    const selectableIndices = useMemo(
      () =>
        items.reduce<number[]>((acc, item, i) => {
          if (item.type !== "header" && item.type !== "sentinel") {
            acc.push(i);
          }
          return acc;
        }, []),
      [items],
    );

    const totalItems = selectableIndices.length;

    // Held-over (stale), still-loading, or failed results don't count as "no
    // matches": pruning stale rows can empty the list before the server has
    // answered, and a failed search says nothing about who matches.
    useEffect(() => {
      if (!onNoMatches || !hasQuery || isStale || isSearching || searchError || totalItems > 0)
        return;
      onNoMatches();
    }, [onNoMatches, hasQuery, isStale, isSearching, searchError, totalItems]);

    // Derive a clamped index inline — avoids effect-based re-renders that cause
    // highlight jumps when totalItems changes during async contact loading.
    const selectedIndex = Math.min(rawSelectedIndex, Math.max(totalItems - 1, 0));

    // Keep refs for latest values so imperative handlers always read fresh state.
    const selectedIndexRef = useRef(selectedIndex);
    selectedIndexRef.current = selectedIndex;
    const totalItemsRef = useRef(totalItems);
    totalItemsRef.current = totalItems;

    // Reset selected index and scroll to top when selection changes (multi-select toggle).
    const prevSelectedSize = useRef(selectedContactIds?.size);
    useEffect(() => {
      if (prevSelectedSize.current === selectedContactIds?.size) return;
      prevSelectedSize.current = selectedContactIds?.size;
      setRawSelectedIndex(0);
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [selectedContactIds?.size]);

    // For inline mode, use the nearest scrollable ancestor as the scroll container.
    useEffect(() => {
      if (!inline || !dropdownRef.current) return;
      let parent = dropdownRef.current.parentElement;
      while (parent) {
        const { overflowY } = getComputedStyle(parent);
        if (overflowY === "auto" || overflowY === "scroll") {
          scrollRef.current = parent;
          return;
        }
        parent = parent.parentElement;
      }
    }, [inline]);

    // Load more contacts when the sentinel at the bottom of the list becomes visible.
    // biome-ignore lint/correctness/useExhaustiveDependencies: re-observe when hasQuery toggles the sentinel in/out of the DOM
    useEffect(() => {
      const sentinel = sentinelRef.current;
      if (!sentinel) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { root: scrollRef.current, rootMargin: "50px" },
      );
      observer.observe(sentinel);
      return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage, hasQuery]);

    // Imperatively scroll a given item into view — called directly from
    // arrow-key handlers (via flushSync) so highlight + scroll update in one frame.
    const scrollToItem = useCallback((selectableIdx: number) => {
      const container = scrollRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-index="${selectableIdx}"]`) as HTMLElement | null;
      if (!el) return;

      // Account for sticky headers (e.g. ContactPicker search input) that overlap the top.
      const stickyHeader = container.querySelector("[data-sticky-header]") as HTMLElement | null;
      const stickyOffset = stickyHeader?.offsetHeight ?? 0;

      // If the item has a section header just above it (previous sibling without data-index),
      // include that header so scrolling to the first item in a section reveals its label.
      let scrollTarget = el;
      const prev = el.previousElementSibling as HTMLElement | null;
      if (prev && !prev.hasAttribute("data-index")) {
        scrollTarget = prev;
      }

      const targetTop = scrollTarget.offsetTop;
      const elBottom = el.offsetTop + el.offsetHeight;
      const viewTop = container.scrollTop;
      const viewBottom = viewTop + container.clientHeight;

      if (elBottom > viewBottom) {
        container.scrollTop = elBottom - container.clientHeight;
      } else if (targetTop < viewTop + stickyOffset) {
        container.scrollTop = targetTop - stickyOffset;
      }
    }, []);

    // Adjust position to anchor bottom when anchorBottom is provided
    // biome-ignore lint/correctness/useExhaustiveDependencies: need to recalculate when list changes size
    useLayoutEffect(() => {
      if (inline) return;
      if (pos.anchorBottom && dropdownRef.current) {
        const dropdownHeight = dropdownRef.current.offsetHeight;
        const newTop = pos.anchorBottom - dropdownHeight - 8;
        // No room above (a box near the top of the screen): open below instead
        // of running off the top, where the first matches can't be seen.
        setAdjustedTop(newTop < window.scrollY ? pos.top : newTop);
      } else {
        setAdjustedTop(pos.top);
      }
    }, [inline, pos.anchorBottom, pos.top, filteredSuggestions.length]);

    // Shared mouse event props for interactive (selectable) items.
    const interactiveProps = useCallback(
      (selectableIdx: number) => ({
        "data-index": selectableIdx,
        onMouseEnter: () => {
          if (!usingKeyboardRef.current) {
            setRawSelectedIndex(selectableIdx);
          }
        },
        onMouseMove: () => {
          usingKeyboardRef.current = false;
        },
      }),
      [],
    );

    // Expose keyboard navigation methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        handleArrowUp: () => {
          usingKeyboardRef.current = true;
          const idx = selectedIndexRef.current;
          if (idx <= 0) return;
          const next = idx - 1;
          flushSync(() => setRawSelectedIndex(next));
          scrollToItem(next);
        },
        handleArrowDown: () => {
          usingKeyboardRef.current = true;
          const idx = selectedIndexRef.current;
          const total = totalItemsRef.current;
          if (idx >= total - 1) return;
          const next = idx + 1;
          flushSync(() => setRawSelectedIndex(next));
          scrollToItem(next);
        },
        handleEnter: () => {
          if (totalItems === 0) return false;
          const item = items[selectableIndices[selectedIndex]];
          if (!item) return false;

          switch (item.type) {
            case "freeText":
              item.onSelect();
              return true;
            case "contact":
              onSelect(item.contact);
              return true;
            case "handle":
              if (onSelectHandle) {
                onSelectHandle(item.contact, item.handle);
              } else {
                onSelect(item.contact);
              }
              return true;
            case "email":
              if (onSelectEmail && isValidEmail(item.query)) {
                onSelectEmail(item.query);
                return true;
              }
              return false;
            default:
              return false;
          }
        },
      }),
      [
        totalItems,
        items,
        selectableIndices,
        selectedIndex,
        onSelect,
        onSelectHandle,
        onSelectEmail,
        scrollToItem,
      ],
    );

    // Don't render if no query (unless showAllWhenEmpty is set)
    if (!query && !showAllWhenEmpty) return null;

    // --- Rendering helpers ---

    const sectionHeader = (label: string, key: string) => (
      <div key={key} className="px-3 pt-2 pb-1">
        <span className="text-[11px] font-semibold text-foreground/70 uppercase tracking-wide">
          {label}
        </span>
      </div>
    );

    // Track which selectable index we're at while iterating.
    // Must stay in sync with selectableIndices — increment for every item type
    // that is NOT "header" or "sentinel" (the same types selectableIndices skips).
    let selectIdx = 0;

    const renderedItems = items.map((item, i): React.ReactNode => {
      switch (item.type) {
        case "header":
          return sectionHeader(item.label, `header-${item.label}-${i}`);

        case "freeText": {
          const idx = selectIdx++;
          if (item.variant === "use") {
            return (
              <div
                key="freetext-use"
                {...interactiveProps(idx)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer",
                  selectedIndex === idx ? "bg-secondary" : "",
                  selectedIndex !== idx && "text-muted-foreground",
                )}
                onMouseDown={(e) => {
                  e.preventDefault();
                  item.onSelect();
                }}
              >
                <Plus className="size-4" />
                {item.label}
              </div>
            );
          }
          // "selected" variant
          return (
            <div
              key="freetext-selected"
              {...interactiveProps(idx)}
              className={cn(
                "flex flex-row items-center gap-2 px-3 py-2 cursor-pointer",
                selectedIndex === idx ? "bg-secondary" : "",
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                item.onSelect();
              }}
            >
              <div className="size-6 shrink-0 rounded-full bg-muted-foreground/30 flex items-center justify-center">
                <Search className="size-3 text-white" />
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{item.label}</div>
              </div>
              <Check className="size-4 text-green-600 shrink-0" />
            </div>
          );
        }

        case "contact": {
          const idx = selectIdx++;
          const { contact, isSelected: isMultiSelected } = item;
          const handle = contact.handles?.find((h) => filterHandleTypes.includes(h.type));
          const isKeyboardSelected = selectedIndex === idx;
          return (
            <div
              key={`${i}-${contact.id}`}
              {...interactiveProps(idx)}
              className={cn(
                "flex flex-row items-center gap-2 px-3 py-2 cursor-pointer",
                isKeyboardSelected ? "bg-secondary" : isMultiSelected ? "bg-secondary/50" : "",
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(contact);
              }}
            >
              <AvatarWithFallback
                size="sm"
                person={{
                  name: contact.name,
                  picture: contact.picture,
                  value: handle?.value || "",
                }}
              />
              <div className="flex flex-col min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{contact.name}</div>
                {handle?.value && (
                  <div className="text-xs text-muted-foreground truncate">{handle.value}</div>
                )}
              </div>
              {isMultiSelected && <Check className="size-4 text-green-600 shrink-0" />}
            </div>
          );
        }

        case "handle": {
          const idx = selectIdx++;
          const { contact, handle, isSelected: isMultiSelected } = item;
          const isKeyboardSelected = selectedIndex === idx;
          return (
            <div
              key={`${i}-${handle.id}`}
              {...interactiveProps(idx)}
              className={cn(
                "flex flex-row items-center gap-2 px-3 py-2 cursor-pointer",
                isKeyboardSelected ? "bg-secondary" : isMultiSelected ? "bg-secondary/50" : "",
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                if (onSelectHandle) {
                  onSelectHandle(contact, handle);
                } else {
                  onSelect(contact);
                }
              }}
            >
              <AvatarWithFallback
                size="sm"
                person={{
                  name: contact.name,
                  picture: contact.picture,
                  value: handle.value || "",
                }}
              />
              <div className="flex flex-col min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{contact.name}</div>
                <div className="text-xs text-muted-foreground truncate">{handle.value}</div>
              </div>
              {isMultiSelected && <Check className="size-4 text-green-600 shrink-0" />}
            </div>
          );
        }

        case "email": {
          const idx = selectIdx++;
          return (
            <div key="email-option" {...interactiveProps(idx)}>
              <EmailOption
                query={item.query}
                isSelected={selectedIndex === idx}
                onSelect={(email) => {
                  if (onSelectEmail && isValidEmail(email)) {
                    onSelectEmail(email);
                  }
                }}
                multiSelect={Boolean(selectedContactIds)}
              />
            </div>
          );
        }

        case "sentinel":
          return (
            <div key="sentinel" ref={sentinelRef} className="flex justify-center py-2">
              {isFetchingNextPage && (
                <span className="text-xs text-muted-foreground">Loading...</span>
              )}
            </div>
          );
        default:
          return null;
      }
    });

    // Catch-all `mousedown` preventDefault: in portal mode the search input's
    // `onBlur` closes the picker, so a click on dropdown chrome (header, gaps,
    // bottom spacer) — anything without its own preventDefault — would steal
    // focus from the input and close the picker. Rows still preventDefault
    // individually; this is a backstop for non-row hit targets.
    const preventBlur = (e: React.MouseEvent) => {
      e.preventDefault();
    };

    if (inline) {
      return (
        <div
          ref={dropdownRef}
          className={cn("py-1 flex flex-col min-w-0", className)}
          onMouseDown={preventBlur}
        >
          {renderedItems}
        </div>
      );
    }

    return createPortal(
      <div
        ref={dropdownRef}
        style={{
          position: "absolute",
          top: adjustedTop,
          left: pos.left,
          width: 300,
          // Re-enable interaction. When the dropdown opens inside a Radix
          // Dialog (e.g. the Email Draft modal), Radix sets
          // `pointer-events: none` on <body> to lock interactions outside
          // the dialog. We portal to <body>, so without this override the
          // entire dropdown — every row included — is unclickable by mouse.
          pointerEvents: "auto",
        }}
        className={cn(
          "z-9999 flex flex-col min-w-0 max-h-64 overflow-hidden",
          "bg-popover text-popover-foreground rounded-xl shadow-lg border border-border",
          className,
        )}
        onMouseDown={preventBlur}
      >
        {/* Section header */}
        <div className="shrink-0 px-3 pt-2 pb-1 flex items-center gap-2">
          <span className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
            People
          </span>
          <span className="text-[10px] text-muted-foreground">tab to accept</span>
        </div>
        <div
          ref={(el) => {
            scrollRef.current = el;
          }}
          className={cn(
            "relative min-h-0 overflow-y-auto",
            "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent hover:scrollbar-thumb-border",
          )}
        >
          {renderedItems}
          {/* Fade overlay — sticky to bottom */}
          <div className="sticky bottom-0 h-3 -mt-3 pointer-events-none bg-gradient-to-t from-popover to-transparent" />
        </div>
        {/* Solid spacer so scrollbar clears rounded corners */}
        <div className="shrink-0 h-3 bg-popover" />
      </div>,
      document.body,
    );
  },
);

ContactAutocompleteDropdown.displayName = "ContactAutocompleteDropdown";
